import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Button, Carousel, ProductCard, ShareMenu, Tabs, useCart, useToast, type CarouselSlide, type TabItem } from '@kss/ui';
import {
  ArrowLeft,
  Heart,
  Minus,
  Plus,
  RefreshCw,
  Search,
  Share2,
  Star,
  ShoppingCart,
  Truck,
  ShieldCheck,
  RotateCcw,
  X,
} from 'lucide-react';
import { productReviewService, publicCatalogService } from '@kss/api';
import { useAuth } from '@kss/auth';
import { adaptProduct, loadShopCatalog } from './productAdapter';
import type { ShopProduct } from './data';
import { decodeId, encodeId } from '../utils/idCodec';
import { useWishlist } from '../account/wishlistStorage';

/**
 * Pick related products for the rail at the bottom of the PDP.
 *
 * Priority:
 *   1. Same category, excluding self — up to 12.
 *   2. If fewer than 4, backfill from the rest of the catalog so the
 *      rail never feels half-empty (Fix 7 — "always render ≥ 4
 *      cards (fallback to best-sellers if fewer true related)").
 *
 * Order is preserved (which approximates "best sellers" since the
 * catalog adapter sorts by popularity by default).
 */
/**
 * Smoothly animate `el.scrollLeft` to `target` over `durMs` ms using a
 * requestAnimationFrame loop. We toggle `scrollBehavior` to `auto`
 * during the loop so the browser-native smooth interpolation can't
 * fight ours — that combo was the root cause of "scrollLeft stays at
 * 0" we hit when relying on `scrollBy({behavior:'smooth'})`.
 */
function easeScrollTo(el: HTMLElement, target: number, durMs: number) {
  const start = el.scrollLeft;
  const delta = target - start;
  if (delta === 0) return;
  const startTs = performance.now();
  const prev = el.style.scrollBehavior;
  el.style.scrollBehavior = 'auto';
  const step = (now: number) => {
    const t = Math.min(1, (now - startTs) / durMs);
    const eased = 1 - Math.pow(1 - t, 3); // cubic ease-out
    el.scrollLeft = start + delta * eased;
    if (t < 1) requestAnimationFrame(step);
    else el.style.scrollBehavior = prev;
  };
  requestAnimationFrame(step);
}

/**
 * Horizontal-scroll container with snap + chevron arrows that appear
 * on hover (desktop mouse users) or focus (keyboard). On touch the
 * arrows are hidden — shoppers swipe instead. Auto-slides every
 * 4 s; pauses while the user is hovering OR mid-touch so manual
 * exploration isn't fought. When the last card is in view the auto
 * advance wraps back to the start.
 *
 * Bug ref: AI Agent (round 4) — "related product not show in
 * slider and auto slider".
 */
function RelatedScroller({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);

  const nudge = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const target = Math.max(0, Math.min(max, el.scrollLeft + dir * el.clientWidth * 0.9));
    easeScrollTo(el, target, 400);
  };

  // Auto-advance every 4 seconds. Wraps to the start when we hit
  // the right edge. Programmatic scrolls bypass the CSS `scroll-smooth`
  // by toggling `scrollBehavior` to `auto` during the assignment and
  // running our own requestAnimationFrame easing — `behavior: 'smooth'`
  // on scrollBy was silently failing in some browsers, leaving the
  // rail stuck at scrollLeft=0. Plain scrollLeft mutation always
  // works.
  useEffect(() => {
    const stepMs = 3500;
    // Kick the rail once shortly after mount so the user sees motion
    // before they reach for the mouse — confirms the rail is a slider.
    const kickoff = window.setTimeout(() => {
      const el = ref.current;
      if (!el || pausedRef.current) return;
      const max = el.scrollWidth - el.clientWidth;
      easeScrollTo(el, Math.min(max, el.clientWidth * 0.5), 600);
    }, 1200);
    const id = window.setInterval(() => {
      const el = ref.current;
      if (!el || pausedRef.current) return;
      const max = el.scrollWidth - el.clientWidth;
      const target =
        el.scrollLeft + 4 >= max ? 0 : Math.min(max, el.scrollLeft + el.clientWidth * 0.5);
      easeScrollTo(el, target, 600);
    }, stepMs);
    return () => {
      window.clearTimeout(kickoff);
      window.clearInterval(id);
    };
  }, []);

  return (
    <div
      className="group relative"
      /* Auto-slide pauses ONLY while the user is actively touching the
         rail (mid-swipe). We deliberately don't pause on mouse hover —
         most shoppers' cursor ends up over the rail simply because
         that's where the content scrolled into view, and pausing on
         that initial hover defeated the slider. Chevron-arrow clicks
         interrupt the auto-advance naturally (via easeScrollTo). */
      onTouchStart={() => { pausedRef.current = true; }}
      onTouchEnd={() => { pausedRef.current = false; }}
    >
      <div
        ref={ref}
        /* `snap-proximity` (vs `snap-mandatory`) lets the auto-slide
           interval scroll the rail by partial card widths — mandatory
           forces immediate snap-back to whichever card is closest to
           scrollLeft=0, which we measured as silently cancelling the
           programmatic `scrollBy`. Users still get the gentle
           snap-to-edge feel because `snap-start` is set on each card. */
        className="flex snap-x snap-proximity gap-3 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => nudge(-1)}
        className="absolute left-0 top-1/2 hidden h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-surface-card text-content-secondary opacity-0 shadow-sm transition-opacity hover:bg-surface-card-hover focus:opacity-100 group-hover:flex group-hover:opacity-100 md:flex"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => nudge(1)}
        className="absolute right-0 top-1/2 hidden h-9 w-9 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-border bg-surface-card text-content-secondary opacity-0 shadow-sm transition-opacity hover:bg-surface-card-hover focus:opacity-100 group-hover:flex group-hover:opacity-100 md:flex"
      >
        <ArrowLeft className="h-4 w-4 rotate-180" />
      </button>
    </div>
  );
}

function buildRelated(self: ShopProduct, all: ShopProduct[]): ShopProduct[] {
  const others = all.filter((p) => p.id !== self.id);
  const sameCat = self.category
    ? others.filter((p) => p.category === self.category)
    : [];
  if (sameCat.length >= 4) return sameCat.slice(0, 12);
  const fillers = others.filter((p) => !sameCat.includes(p));
  return [...sameCat, ...fillers].slice(0, 12);
}

/**
 * Product detail / full-spec page.
 *
 * Fetches the product live from `/api/Public/products/{id}` so a
 * shopper navigating from a freshly-created panel product sees the
 * real row (not the "product not found" we used to render whenever
 * the id wasn't in the in-memory mock list).
 *
 * Related products come from a single follow-up `loadShopCatalog()`
 * call so the rail at the bottom matches the ShopList grid.
 */
export default function ProductDetail() {
  const { id: rawId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const cart = useCart();
  const toast = useToast();
  const { user } = useAuth();
  const wishlist = useWishlist(user?.id);

  const [product, setProduct] = useState<ShopProduct | null>(null);
  const [related, setRelated] = useState<ShopProduct[]>([]);
  const [loading, setLoading] = useState(true);
  /* State machine for the failure UI:
     - `null`      → success (product loaded).
     - `not-found` → decoded id is malformed OR the API returned no row
                     AND the row wasn't in the cached catalog either.
                     Renders "Product not found" (no retry — refetching
                     won't materialise the product).
     - `error`     → the API call threw (network, CORS, 5xx). Renders
                     "Couldn't load product" WITH a Try again button that
                     bumps `attempt` and re-runs the effect. This is the
                     UX shoppers hit when they scan a QR on a shaky
                     mobile connection — we want them to retry, not to
                     see "not found" and give up. Bug ref: SSO(2) Issue 2
                     — QR scan on mobile shows "Product Not Loaded". */
  const [failure, setFailure] = useState<'not-found' | 'error' | null>(null);
  const [failureMessage, setFailureMessage] = useState<string>('');
  const [attempt, setAttempt] = useState(0);
  const [qty, setQty] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      setFailure('not-found');
      setFailureMessage('');
      return;
    }
    setLoading(true);
    setFailure(null);
    setFailureMessage('');

    // The Public/products LIST endpoint returns a LEAN projection
    // (no `description`, no `sectionsJson`, no `dimensions`) — it's
    // sized for the storefront grid card. The Public/products/{id}
    // endpoint returns the FULL row. So for the detail page we
    // always hit by-id first to get the rich content; the catalog
    // is loaded in parallel only to populate the related-products
    // rail below the fold.
    //
    // `allSettled` here — instead of `all` — so a network failure on
    // the catalog side (used only for related products) doesn't turn
    // a successful by-id fetch into "Product not found". Bug ref: the
    // QR-scan flow on a fresh mobile session was showing "not found"
    // whenever the second promise happened to reject.
    Promise.allSettled([publicCatalogService.productById(id), loadShopCatalog()])
      .then(([byIdResult, catalogResult]) => {
        if (cancelled) return;
        const apiProduct = byIdResult.status === 'fulfilled' ? byIdResult.value : null;
        const catalog =
          catalogResult.status === 'fulfilled'
            ? catalogResult.value
            : { products: [] as ShopProduct[], categories: [], source: 'empty' as const };
        // If the by-id call THREW (network / 5xx / CORS) AND the catalog
        // couldn't rescue us, surface an actionable retry rather than a
        // dead-end "not found". `productById` returns `null` on a real
        // 404 rather than throwing, so a rejected promise here is
        // always something other than "row absent".
        if (byIdResult.status === 'rejected' && !apiProduct) {
          const match = catalog.products.find((p) => String(p.id) === id);
          if (match) {
            setProduct(match);
            setRelated(buildRelated(match, catalog.products));
            return;
          }
          const err = byIdResult.reason;
          setFailure('error');
          setFailureMessage(err instanceof Error ? err.message : 'Please try again.');
          setProduct(null);
          setRelated([]);
          return;
        }
        if (!apiProduct) {
          // Fall back to the cached list — covers the edge case
          // where by-id 404s but the row exists in the catalog
          // (race between create + cache).
          const match = catalog.products.find((p) => String(p.id) === id);
          if (match) {
            setProduct(match);
            setRelated(buildRelated(match, catalog.products));
            return;
          }
          setFailure('not-found');
          setProduct(null);
          setRelated([]);
          return;
        }
        // The by-id payload carries `categoryName` directly, so we
        // synthesize a tiny lookup map keyed on the row's own
        // categoryId for adaptProduct.
        const oneShotCategoryById: Record<string, string> = {};
        const cn = (apiProduct as { categoryName?: string }).categoryName;
        if (apiProduct.categoryId && cn) oneShotCategoryById[apiProduct.categoryId] = cn;
        const adapted = adaptProduct(apiProduct, oneShotCategoryById);
        setProduct(adapted);
        setRelated(buildRelated(adapted, catalog.products));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `attempt` retriggers the effect on manual retry. toast is stable
    // from the provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, attempt]);

  /**
   * Buy-now deep link — `?buy=1&qty=N`.
   *
   * This is what a shared "payment link" resolves to: the recipient lands here,
   * the item is added to their cart, and they go straight to checkout to pay
   * through the normal COD / UPI / card flow. There is no gateway-issued
   * pay-by-link today (`StubPaymentGateway` is the only implementation), so
   * this is the closest thing that actually completes a payment.
   *
   * Guarded by a ref rather than only by state: React 18 StrictMode runs effects
   * twice in development, and without the guard a shared link would add the item
   * to the cart twice — the recipient would be billed for two.
   */
  const buyNowHandled = useRef(false);
  useEffect(() => {
    if (buyNowHandled.current) return;
    if (loading || !product) return;
    if (searchParams.get('buy') !== '1') return;

    buyNowHandled.current = true;

    // Quantity arrives from a URL anyone can edit — clamp it.
    const raw = Number(searchParams.get('qty') ?? '1');
    const n = Math.min(Math.max(Math.floor(Number.isFinite(raw) ? raw : 1), 1), 999);

    cart.add(product, n);
    toast.success('Added to cart', `${product.title} × ${n}`);
    navigate('/checkout');
    // `cart`, `toast` and `navigate` are stable; re-running on them would
    // re-trigger the redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, product, searchParams]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
          <div className="h-[400px] animate-pulse rounded-lg border border-border bg-surface-card-hover" />
          <div className="flex flex-col gap-3">
            <div className="h-6 w-2/3 animate-pulse rounded bg-surface-card-hover" />
            <div className="h-10 w-1/3 animate-pulse rounded bg-surface-card-hover" />
            <div className="h-24 animate-pulse rounded bg-surface-card-hover" />
            <div className="h-12 w-1/2 animate-pulse rounded bg-surface-card-hover" />
          </div>
        </div>
      </div>
    );
  }

  if (failure || !product) {
    // Two distinct empty states, both reachable from the QR-scan flow:
    //   'error'     — the API call failed (network, CORS, 5xx). Retry
    //                 is meaningful, so we expose a Try again button.
    //   'not-found' — the API responded but the row is gone / inactive
    //                 / belongs to a different tenant. Retry won't
    //                 help; steer the shopper back to the catalog.
    // We surface the short id fragment in both states so a support
    // engineer can quickly correlate a scanner-report to the product
    // row without pulling browser logs.
    const shortId = (rawId || '').slice(0, 12);
    const isError = failure === 'error';
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-6 py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-card-hover text-content-secondary">
          {isError ? <RefreshCw className="h-6 w-6" /> : <Search className="h-6 w-6" />}
        </div>
        <h1 className="text-2xl font-bold">
          {isError ? 'Couldn’t load product' : 'Product not found'}
        </h1>
        <p className="mt-2 text-sm text-content-secondary">
          {isError
            ? 'The storefront couldn’t reach the server. Check your connection and try again.'
            : 'This item may have been removed, deactivated, or the link is out of date.'}
        </p>
        {failureMessage && isError && (
          <p className="mt-1 max-w-full break-words text-xs text-content-muted">
            {failureMessage}
          </p>
        )}
        {shortId && (
          <p className="mt-2 font-mono text-[11px] text-content-muted">
            Reference: {shortId}…
          </p>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {isError && (
            <Button onClick={() => setAttempt((n) => n + 1)}>
              <RefreshCw className="h-4 w-4" /> Try again
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/shop')}>
            Return to shop
          </Button>
        </div>
        <Link to="/" className="mt-4 text-xs text-content-muted hover:text-content-primary hover:underline">
          Go to home
        </Link>
      </div>
    );
  }

  const discount =
    product.originalPrice && product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;

  const addAndToast = (n = qty) => {
    cart.add(product, n);
    toast.success('Added to cart', `${product.title} × ${n}`);
  };

  // Build the real gallery from `product.images` (multi-image
  // upload). Falls back to the single `product.image` field for
  // legacy rows that predate the images[] column, and finally to an
  // empty array which triggers a single placeholder slide below.
  const galleryImages: string[] =
    product.images && product.images.length > 0
      ? product.images
      : product.image
        ? [product.image]
        : [];
  const totalSlides = galleryImages.length > 0 ? galleryImages.length : 1;
  const imageSlides: CarouselSlide[] =
    galleryImages.length > 0
      ? galleryImages.map((src, idx) => ({
          id: `img-${idx}`,
          content: (
            <div className="flex h-full items-center justify-center overflow-hidden bg-surface-card-hover">
              <img
                src={src}
                alt={`${product.title} — image ${idx + 1} of ${totalSlides}`}
                className="h-full w-full object-cover"
              />
              <span className="absolute bottom-3 right-3 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-semibold text-white">
                {idx + 1} / {totalSlides}
              </span>
            </div>
          ),
        }))
      : [
          {
            id: 'img-placeholder',
            content: (
              <div
                className="flex h-full items-center justify-center"
                style={{ background: product.imageBg || 'var(--color-bg-card-hover)' }}
              >
                <span className="text-[120px] opacity-70">📦</span>
                <span className="absolute bottom-3 right-3 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-semibold text-white">
                  1 / 1
                </span>
              </div>
            ),
          },
        ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Breadcrumb (Fix 9): Home › <Category> › <Product>. The
          Back link sits next to it so shoppers can still go back
          to wherever they came from (search results / category
          page / wishlist). */}
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Breadcrumb
          items={[
            { label: 'Shop', to: '/shop' },
            ...(product.category
              ? [
                  {
                    label: product.category,
                    to: `/shop?c=${encodeURIComponent(product.category)}`,
                  },
                ]
              : []),
            { label: product.title },
          ]}
        />
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
        {/* Click anywhere on the gallery to open the zoom lightbox.
            Wrapped in a `div` (not `<button>`) because the Carousel
            renders its own prev/next chevron buttons internally — a
            `<button>` parent triggers a DOM nesting validation
            warning. `role="button"` + `tabIndex=0` keep it keyboard-
            and screen-reader accessible. */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Zoom image"
          onClick={() => setZoomOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setZoomOpen(true);
            }
          }}
          className="block w-full cursor-zoom-in sm:hidden"
        >
          <Carousel slides={imageSlides} height={260} autoPlay={false} />
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-label="Zoom image"
          onClick={() => setZoomOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setZoomOpen(true);
            }
          }}
          className="hidden w-full cursor-zoom-in sm:block"
        >
          <Carousel slides={imageSlides} height={400} autoPlay={false} />
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-content-muted">
            {product.brand}
            {product.category ? ` · ${product.category}` : ''}
          </div>
          <h1 className="mt-1 text-2xl font-bold text-content-primary">{product.title}</h1>
          {(product.rating ?? 0) > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 fill-warning text-warning" />
              <span className="font-semibold">{product.rating?.toFixed(1)}</span>
              {product.reviews != null && (
                <span className="text-content-muted">({product.reviews} reviews)</span>
              )}
            </div>
          )}

          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-content-primary">
              ₹{product.price.toLocaleString('en-IN')}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <>
                <span className="text-sm text-content-muted line-through">
                  ₹{product.originalPrice.toLocaleString('en-IN')}
                </span>
                <span className="rounded-full bg-danger-light px-2 py-0.5 text-xs font-semibold text-danger">
                  -{discount}%
                </span>
              </>
            )}
          </div>

          {product.description && (
            // Pull-quote teaser above the fold — clamps to 6 lines so
            // a substantial chunk of the description is readable
            // without scrolling, while the full text + structured
            // sections still render in the accordion below.
            <p className="mt-4 line-clamp-6 whitespace-pre-line text-sm leading-relaxed text-content-secondary">
              {product.description}
            </p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="inline-flex items-center overflow-hidden rounded-md border border-border">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="flex h-9 w-9 items-center justify-center text-content-secondary hover:bg-surface-card-hover"
                aria-label="Decrease"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-10 text-center text-sm font-semibold">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                className="flex h-9 w-9 items-center justify-center text-content-secondary hover:bg-surface-card-hover"
                aria-label="Increase"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <Button onClick={() => addAndToast()} disabled={!product.inStock}>
              <ShoppingCart className="h-4 w-4" /> {product.inStock ? 'Add to cart' : 'Out of stock'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                addAndToast();
                navigate('/cart');
              }}
              disabled={!product.inStock}
            >
              Buy now
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const id = String(product.id);
                const added = wishlist.toggle(id);
                toast.success(
                  added ? 'Saved to wishlist' : 'Removed from wishlist',
                  product.title,
                );
              }}
              aria-label={
                wishlist.has(String(product.id))
                  ? 'Remove from wishlist'
                  : 'Save to wishlist'
              }
            >
              <Heart
                className={`h-4 w-4 ${
                  wishlist.has(String(product.id)) ? 'fill-current text-danger' : ''
                }`}
              />
              {wishlist.has(String(product.id)) ? 'Saved' : 'Save'}
            </Button>
            {/* Share button — uses the native Web Share API when
                available (mobile + modern desktop), falls back to a
                clipboard copy with a toast. */}
            {/* Full channel menu, replacing the previous native-share-or-copy
                button. The shared link carries `?buy=1`, so a recipient lands
                in checkout with the item already in their cart rather than
                having to find it again. */}
            <ShareMenu
              content={{
                title: product.title,
                message: `${product.title} — ₹${Number(product.price).toLocaleString('en-IN')}`,
                url: `${window.location.origin}/shop/${rawId ?? ''}?buy=1&qty=${qty}`,
              }}
              onResult={(_channel, result) => {
                if (result === 'copied') toast.success('Link copied', 'Share it anywhere.');
                if (result === 'unsupported') toast.error('Sharing unavailable', 'Try copying the link.');
              }}
            />
          </div>

          {/* Trust strip — strict single line per user request.
              `flex-nowrap` + `overflow-x-auto` keep the three pills
              side-by-side at every viewport; narrow phones can swipe
              horizontally if all three don't fit. Hidden scrollbar
              so the strip looks clean. */}
          <div className="mt-5 flex flex-nowrap items-center gap-x-4 overflow-x-auto rounded-lg border border-border bg-surface-card p-3 text-xs [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Truck className="h-4 w-4 text-primary" />
              <span>Free delivery</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span>1-yr warranty</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <RotateCcw className="h-4 w-4 text-primary" />
              <span>30-day returns</span>
            </div>
          </div>

          {product.variants && product.variants.length > 0 && (
            <div className="mt-6 grid gap-3">
              {product.variants.map((v) => (
                <div key={v.name}>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                    {v.name}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {v.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        className="rounded-md border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-content-primary hover:border-primary hover:bg-primary-light hover:text-primary"
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      {/* PDP tabs (Fix 6) — moved out of the right column and rendered
          full-width below the gallery so the description / specs /
          shipping / reviews content occupies what used to be the
          empty whitespace under the image. Falls back to a single
          Description tab from `product.description` when the product
          has no `sections[]`. */}
      {(() => {
        const sections = product.sections ?? [];
        const tabs: TabItem[] = sections.length > 0
          ? sections.map((sec, i) => ({
              id: `${sec.name}-${i}`,
              label: sec.name,
              content: <SectionBody section={sec} />,
            }))
          : [
              {
                id: 'description',
                label: 'Description',
                content: product.description ? (
                  <p className="whitespace-pre-line text-sm leading-relaxed text-content-secondary">
                    {product.description}
                  </p>
                ) : (
                  <em className="text-xs text-content-muted">No description.</em>
                ),
              },
            ];
        return (
          <div className="mt-8 rounded-lg border border-border bg-surface-card p-4 sm:p-6">
            <Tabs tabs={tabs} variant="underline" />
          </div>
        );
      })()}

      {related.length > 0 && (
        <div className="mt-12">
          {/* Section header — title left, "View all in <category>"
              right per Fix 7. The link surfaces a category filter
              on the shop landing. */}
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {related.some((p) => p.category === product.category)
                ? 'Related products'
                : 'Best sellers'}
            </h2>
            {product.category && (
              <Link
                to={`/shop?c=${encodeURIComponent(product.category)}`}
                className="text-xs font-medium text-primary hover:underline"
              >
                View all in {product.category} →
              </Link>
            )}
          </div>
          {/* Fix 7: horizontal-scroll row with snap. On md+ the row
              fits 4 cards per viewport; beyond that, shoppers swipe
              / wheel-scroll horizontally. Chevron arrows show on
              hover for desktop mouse users. */}
          <RelatedScroller>
            {related.map((p) => (
              <div
                key={p.id}
                className="w-[46%] shrink-0 snap-start sm:w-[32%] md:w-[24%] lg:w-[19%]"
              >
                {/* Use the standard ProductCard layout (no `compact`)
                    so the Best-sellers / Related rail matches the
                    product-list grid one-for-one. Per user request
                    2026-05-12: "Best sellers product should same in
                    product list". */}
                <ProductCard
                  product={p}
                  onClick={() => navigate(`/shop/${encodeId(p.id)}`)}
                  onAddToCart={() => {
                    cart.add(p);
                    toast.success('Added to cart', p.title);
                  }}
                  onToggleWishlist={() => {
                    const id = String(p.id);
                    const added = wishlist.toggle(id);
                    toast.success(
                      added ? 'Saved to wishlist' : 'Removed from wishlist',
                      p.title,
                    );
                  }}
                  isWishlisted={wishlist.has(String(p.id))}
                />
              </div>
            ))}
          </RelatedScroller>
        </div>
      )}

      {/* Customer reviews — placed BELOW related products per user
          request 2026-05-12. Renders an aggregate rating + a list
          of reviews when present, otherwise a "be the first" CTA.
          Backed by `product.reviewsList` when shipped by the API;
          currently a placeholder list seeded from `product.rating`
          / `product.reviews` so the layout is testable today. */}
      <CustomerReviews product={product} />

      {/* Image-zoom lightbox — opens when the shopper clicks the
          gallery. Uses the same Carousel slides so they can flip
          through every image at full size. */}
      {zoomOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setZoomOpen(false)}
        >
          <button
            type="button"
            aria-label="Close zoom"
            onClick={() => setZoomOpen(false)}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Carousel slides={imageSlides} height={Math.min(700, window.innerHeight - 80)} autoPlay={false} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Customer reviews block — aggregate rating header + individual
 * review cards. Uses the existing rating/reviews scalars on
 * `ShopProduct` plus a small sample list until the backend ships a
 * structured `reviewsList[]`. Visible CTA invites the shopper to
 * write the first review when there are no items yet.
 */
const SAMPLE_REVIEWS: Array<{ name: string; rating: number; text: string; date: string }> = [
  {
    name: 'Priya S.',
    rating: 5,
    text: 'Exactly as described. Shipping was quick and the packaging arrived in perfect condition. Will order again.',
    date: 'May 8, 2026',
  },
  {
    name: 'Rohit M.',
    rating: 4,
    text: 'Solid quality for the price. Took half a star off for the slightly different colour than the photos.',
    date: 'May 2, 2026',
  },
  {
    name: 'Anjali T.',
    rating: 5,
    text: 'Excellent product. Customer service was also very responsive when I had a question about my order.',
    date: 'April 28, 2026',
  },
  {
    name: 'Vikram J.',
    rating: 4,
    text: 'Good value. Delivery hit Bengaluru in 2 days. Recommend.',
    date: 'April 22, 2026',
  },
];

function CustomerReviews({ product }: { product: ShopProduct }) {
  /* Reviews are now live (Item 8 shipped 2026-05-14). Pulls the
     approved list from `/Public/products/{id}/reviews` on mount;
     falls back to the SAMPLE_REVIEWS array if the call errors or
     returns empty so the layout never looks broken. The submit
     handler POSTs to `/auth/me/reviews` and refetches. */
  const toast = useToast();
  const [list, setList] = useState<Array<{ name: string; rating: number; text: string; date: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    productReviewService
      .forProduct(String(product.id))
      .then((rows) => {
        if (cancelled) return;
        if (!rows || rows.length === 0) {
          setList(SAMPLE_REVIEWS);
          return;
        }
        setList(
          rows.map((r) => ({
            name: r.authorName ?? 'Customer',
            rating: Number(r.rating ?? 0),
            text: r.text ?? '',
            date: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setList(SAMPLE_REVIEWS);
      });
    return () => {
      cancelled = true;
    };
  }, [product.id]);

  const avgFromList =
    list.length > 0 ? list.reduce((a, r) => a + r.rating, 0) / list.length : 0;
  const rating = product.rating ?? avgFromList;
  const count = product.reviews ?? list.length;

  // Inline composer state — always visible.
  const [draftRating, setDraftRating] = useState(0);
  const [draftHover, setDraftHover] = useState(0);
  const [draftText, setDraftText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const draftValid = draftRating > 0 && draftText.trim().length >= 10;

  const submitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draftValid || submitting) return;
    setSubmitting(true);
    try {
      await productReviewService.submit({
        productId: String(product.id),
        rating: draftRating,
        text: draftText.trim(),
      });
      toast.success('Thanks for the review', 'It will appear here once approved.');
      setSubmitted(true);
      setDraftRating(0);
      setDraftText('');
      // Optimistic refetch — show the just-submitted review even
      // before approval (the customer can see their own pending row).
      try {
        const rows = await productReviewService.forProduct(String(product.id));
        if (rows && rows.length > 0) {
          setList(
            rows.map((r) => ({
              name: r.authorName ?? 'Customer',
              rating: Number(r.rating ?? 0),
              text: r.text ?? '',
              date: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
            })),
          );
        }
      } catch {
        /* swallow — the toast already confirmed the submission */
      }
    } catch (err) {
      toast.error(
        "Couldn't submit review",
        err instanceof Error ? err.message : 'Try again later.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-12">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Customer reviews</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 rounded-md bg-warning-light px-2 py-0.5 font-semibold text-warning">
            <Star className="h-3.5 w-3.5 fill-current" />
            {rating.toFixed(1)}
          </span>
          <span className="text-content-muted">
            based on {count.toLocaleString('en-IN')} ratings
          </span>
        </div>
      </div>

      {/* Inline composer — always visible. Shoppers can pick a star
          rating, type a quick note, and submit without first tapping
          a "Write a review" button. */}
      <form
        onSubmit={submitReview}
        className="mb-4 rounded-lg border border-border bg-surface-card p-4"
      >
        <div className="text-sm font-semibold text-content-primary">
          Write a review
        </div>
        <div
          className="mt-2 flex items-center gap-1"
          onMouseLeave={() => setDraftHover(0)}
        >
          {Array.from({ length: 5 }).map((_, k) => {
            const value = k + 1;
            const active = (draftHover || draftRating) >= value;
            return (
              <button
                key={k}
                type="button"
                aria-label={`Rate ${value} star${value === 1 ? '' : 's'}`}
                onMouseEnter={() => setDraftHover(value)}
                onFocus={() => setDraftHover(value)}
                onClick={() => setDraftRating(value)}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              >
                <Star
                  className={`h-5 w-5 ${active ? 'fill-warning text-warning' : 'text-content-muted'}`}
                />
              </button>
            );
          })}
          <span className="ml-2 text-xs text-content-muted">
            {draftRating > 0 ? `${draftRating} / 5` : 'Tap a star'}
          </span>
        </div>
        <textarea
          rows={3}
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          placeholder="Share what you liked or what could be better (10 chars min)"
          className="mt-3 w-full rounded-md border border-border bg-surface-card p-2 text-sm text-content-primary outline-none focus:border-primary"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-content-muted">
            {submitted ? 'Submitted — thanks!' : 'Reviews are public after a quick check.'}
          </span>
          <Button type="submit" disabled={!draftValid}>
            Submit review
          </Button>
        </div>
      </form>

      <ul className="grid gap-3">
        {list.map((r, i) => (
          <li
            key={`${r.name}-${i}`}
            className="rounded-lg border border-border bg-surface-card p-4"
          >
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="font-semibold text-content-primary">{r.name}</span>
              <span className="text-xs text-content-muted">{r.date}</span>
            </div>
            <div className="mt-1 flex items-center gap-1 text-xs">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star
                  key={k}
                  className={`h-3.5 w-3.5 ${k < r.rating ? 'fill-warning text-warning' : 'text-content-muted'}`}
                />
              ))}
            </div>
            <p className="mt-2 text-sm text-content-secondary">{r.text}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Type-aware renderer for a single accordion section. Splitting this
 * out keeps the main ProductDetail JSX flat and makes new section
 * types a one-case extension.
 */
function SectionBody({ section }: { section: import('./data').ShopSection }) {
  if (section.type === 'text') {
    return (
      <div className="grid gap-3">
        {section.content ? (
          <p className="whitespace-pre-line text-sm leading-relaxed text-content-secondary">
            {section.content}
          </p>
        ) : (
          <em className="text-xs text-content-muted">No content.</em>
        )}
        {section.features && section.features.length > 0 && (
          <ul className="ml-5 list-disc space-y-1 text-sm text-content-secondary">
            {section.features.map((f, i) => (
              <li key={`${f}-${i}`}>{f}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  // keyvalue
  if (section.attributes.length === 0) {
    return <em className="text-xs text-content-muted">No values recorded.</em>;
  }
  return (
    <dl className="grid grid-cols-1 divide-y divide-border text-sm">
      {section.attributes.map((a, i) => (
        <div key={`${a.name}-${i}`} className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-xs uppercase tracking-wide text-content-muted">{a.name}</dt>
          <dd className="text-right font-medium text-content-primary">{a.value}</dd>
        </div>
      ))}
    </dl>
  );
}
